import { Block } from '../classes/Block';
import { VideoSettings } from '../components/VideoSettings';
import { getApiUrl } from './api';
import { authFetch } from './authFetch';
import { CardGroup } from './cardGroups';
import { CardFramingSettings } from './cardFraming';
import { ApiBlock, ApiCardGroup } from './apiTypes';
import { sanitizeFileNameSegment } from './stringSanitization';
import { toFontUrlMap } from './fontHelpers';
import { buildCardsApiPayload, transformBlockToApiFormat } from './renderPayload';

export type ExportFormat = 'png' | 'tiff' | 'pdf';
export type ExportLayout = 'portrait' | 'landscape';

export interface ExportConfig {
  format: ExportFormat;
  mimeType: string;
  fileExtension: string;
  overrideColors?: boolean; // For PDF: force black text and white background
}

export const EXPORT_CONFIGS: Record<ExportFormat, ExportConfig> = {
  png: {
    format: 'png',
    mimeType: 'image/png',
    fileExtension: 'png',
  },
  tiff: {
    format: 'tiff',
    mimeType: 'image/tiff',
    fileExtension: 'tiff',
  },
  pdf: {
    format: 'pdf',
    mimeType: 'application/pdf',
    fileExtension: 'pdf',
    overrideColors: true,
  },
};

export interface ExportOptions {
  projectId: string;
  projectName: string;
  blocks: Block[];
  cardGroups?: CardGroup[];
  columnDefs: any[];
  videoSettings: VideoSettings;
  config: ExportConfig;
  transparent?: boolean;
  tiffSplitCards?: boolean;
  layout?: ExportLayout;
}

function getResponseDownloadName(response: Response): string | null {
  const contentDisposition: string | null = response.headers.get('content-disposition');
  if (!contentDisposition) {
    return null;
  }

  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1]);
  }

  const asciiMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
  return asciiMatch?.[1] ?? null;
}

function buildStaticExportDownloadName(
  projectName: string,
  format: string,
  resolution: string | undefined,
  aspectRatio: string | undefined,
  extension: string,
  date: Date = new Date()
): string {
  const dateSegment: string = date.toISOString().split('T')[0];
  return [
    sanitizeFileNameSegment(projectName, { fallback: 'project', lowercase: false }),
    sanitizeFileNameSegment(format, { fallback: extension, lowercase: false }),
    sanitizeFileNameSegment(resolution, { fallback: 'res', lowercase: false }),
    sanitizeFileNameSegment(aspectRatio, { fallback: 'aspect' }),
    dateSegment,
  ].join('_') + `.${extension}`;
}

function getExportCardFramingSettings(
  cardFraming: CardFramingSettings | undefined,
  overrideColors: boolean
): CardFramingSettings | undefined {
  if (!cardFraming) {
    return undefined;
  }

  if (!overrideColors) {
    return cardFraming;
  }

  return {
    background_type: 'color',
    background_color: '#FFFFFF',
    content_area: cardFraming.content_area,
  };
}

export async function exportToFormat(options: ExportOptions): Promise<void> {
  const {
    projectId,
    projectName,
    blocks,
    cardGroups = [],
    columnDefs,
    videoSettings,
    config,
    transparent = false,
    tiffSplitCards = false,
    layout,
  } = options;
  const shouldGroupByCards: boolean = videoSettings.mode === 'cards';
  const overrideColors: boolean = config.overrideColors ?? false;
  const exportData: ApiBlock[] | ApiCardGroup[] = shouldGroupByCards
    ? buildCardsApiPayload({
      blocks,
      cardGroups,
      columnDefs,
      propertiesMode: 'render',
      overrideColors,
    })
    : blocks
      .filter((block: Block) => !block.isHidden)
      .map((block: Block) => transformBlockToApiFormat(block, {
        columnDefs,
        propertiesMode: 'render',
        overrideColors,
      }));

  const payload = {
    video_settings: {
      background_color: overrideColors ? '#FFFFFF' : videoSettings.background_color,
      fps_num: videoSettings.fps_num,
      resolution: videoSettings.resolution,
      transparent,
      video_time: videoSettings.video_time,
      aspect_ratio: videoSettings.aspect_ratio,
      fonts: JSON.stringify(toFontUrlMap(videoSettings.fonts)),
      logos: JSON.stringify(videoSettings.logos),
      mode: videoSettings.mode,
      ...(shouldGroupByCards ? {
        card_framing: getExportCardFramingSettings(videoSettings.card_framing, overrideColors),
      } : {}),
    },
    data: exportData,
    transparent,
  };

  const searchParams = new URLSearchParams({
    projectId,
    format: config.format,
  });

  if (config.format === 'tiff' && shouldGroupByCards) {
    searchParams.set('tiffSplitCards', String(tiffSplitCards));
  }
  if (config.format === 'pdf' && shouldGroupByCards && layout) {
    searchParams.set('layout', layout);
  }

  const downloadExtension: string = config.format === 'tiff' && shouldGroupByCards && tiffSplitCards
    ? 'zip'
    : config.fileExtension;
  const requestedDownloadName: string = buildStaticExportDownloadName(
    projectName,
    config.format,
    videoSettings.resolution,
    videoSettings.aspect_ratio,
    downloadExtension
  );
  searchParams.set('downloadName', requestedDownloadName);

  const response = await authFetch(`${getApiUrl()}/export-png?${searchParams.toString()}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error(`Failed to export ${config.format.toUpperCase()}`);
  }

  const arrayBuffer = await response.arrayBuffer();
  const responseMimeType: string = response.headers.get('content-type') || config.mimeType;
  const responseDownloadName: string | null = getResponseDownloadName(response);
  const blob = new Blob([arrayBuffer], { type: responseMimeType });
  const url = window.URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = responseDownloadName ?? requestedDownloadName;
  document.body.appendChild(link);
  link.click();

  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}
